import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, lastValueFrom } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';

import { ClientFilterObject } from '@common/filter.decorator';
import { Cluster } from '@common/entities';
import {
  ClusterDataReviewRequestResponse,
  ClusterDataReviewStatusResponse,
  ClusterExportRequestResponse,
  ClusterExportStatusResponse,
} from '@common/dtos';

import { C } from 'src/app/@shared/constants';
import { ClusterExportMode } from 'src/app/@shared/export/export-field-resolver';
import { ClusterModel } from 'src/app/cluster/@shared/cluster.model';
import { ContentOptions, ContentService } from 'src/app/@core/content.service';
import { ProtoUrl } from 'src/app/@shared/protoUrl';

@Injectable({
  providedIn: 'root',
})
export class ClusterService extends ContentService<ClusterModel, Cluster> {
  private path = 'clusters';
  constructor(private http: HttpClient) {
    super();
  }

  public loadContent(id: string, options: ContentOptions<Cluster> = {}): Observable<ClusterModel[] | null> {
    if (!options.type) {
      console.warn('[ClusterService] loadContent(id, options) no type provided -> defaults to loadListContent');
    }

    if (options.type !== 'detail') {
      return this.loadListContent(id, options);
    }

    return this.loadDetailContent(id, options).pipe(map((item: ClusterModel | null) => {
      return item
        ? [item]
        : null;
    }));
  }

  public create(attributes: Partial<Cluster>, storeId: string): Promise<ClusterModel> {
    return this.http.post<Cluster>(ProtoUrl.getUrl(this.path), attributes)
      .pipe(map((item: Cluster) => { return new ClusterModel(item); }),
        tap((item: ClusterModel) => {
          if (this.metadataStore[storeId]) {
            this.metadataStore[storeId].totalCount += 1;
          }

          this.updateItem(item, storeId, true);
        }))
      .toPromise();
  }

  public patch(id: string, attributes: Partial<Cluster>): Promise<ClusterModel> {
    return this.http.patch<Cluster>(ProtoUrl.getUrl(`${this.path}/${id}`), attributes)
      .pipe(map((item: Cluster) => { return new ClusterModel(item); }),
        tap((item: ClusterModel) => {
          this.updateItem(item);
        }))
      .toPromise();
  }

  public requestDataReview(id: string): Promise<ClusterDataReviewRequestResponse> {
    return lastValueFrom(this.http.post<ClusterDataReviewRequestResponse>(ProtoUrl.getUrl(`${this.path}/${id}/data-review`), {}));
  }

  public requestDataReviewStatus(id: string): Promise<ClusterDataReviewStatusResponse> {
    return lastValueFrom(this.http.get<ClusterDataReviewStatusResponse>(ProtoUrl.getUrl(`${this.path}/${id}/data-review/status`)));
  }

  public requestClusterExport(id: string, exportMode: ClusterExportMode, selectedFieldKeys: string[]): Promise<ClusterExportRequestResponse> {
    const normalizedSelectedFieldKeys = selectedFieldKeys || [];

    return lastValueFrom(this.http.post<ClusterExportRequestResponse>(ProtoUrl.getUrl(`${this.path}/${id}/export`), {
      exportMode,
      selectedFieldKeys: normalizedSelectedFieldKeys,
    }));
  }

  public requestClusterExportStatus(id: string): Promise<ClusterExportStatusResponse> {
    return lastValueFrom(this.http.get<ClusterExportStatusResponse>(ProtoUrl.getUrl(`${this.path}/${id}/export-status`)));
  }

  public downloadExportFile(fileId: string): Promise<Blob> {
    return lastValueFrom(this.http.get(ProtoUrl.getUrl(`files/${fileId}/download`), {
      responseType: 'blob',
    }));
  }

  public delete(id: string): Promise<boolean> {
    return this.http.delete<Cluster>(ProtoUrl.getUrl(`${this.path}/${id}`))
      .pipe(map(() => {
        return true;
      }),
      tap(() => {
        this.removeItem(id);
      }))
      .toPromise();
  }

  protected loadListContent(id: string, options: ContentOptions<Cluster>): Observable<ClusterModel[]> {
    const filter: ClientFilterObject<Cluster> = this.getNormalizedFilter(id, options.filter);
    const url = ProtoUrl.getUrl(this.path, {
      filter,
    });

    console.info('[ClusterService] load items', url);
    let totalCount: number;

    return this.http.get<Cluster[]>(url, {
      observe: 'response',
    })
      .pipe(
        timeout(C.DEFAULT_TIMEOUT),
        tap((response: HttpResponse<Cluster[]>) => {
          const { headers } = response;

          totalCount = parseInt(headers.get('x-total-count')) || (response.body?.length || 0);
        }),
        map((response: HttpResponse<Cluster[]>) => { return response.body || []; }),
        map((items: Cluster[]) => { return items.map((item) => { return new ClusterModel(item); }); }),
        tap((items: ClusterModel[]) => {
          return this.setItems(items, id, filter, totalCount);
        }),
      );
  }

  protected loadDetailContent(id: string, options: ContentOptions<Cluster>): Observable<ClusterModel> {
    const { filter } = options;
    const url = ProtoUrl.getUrl(`${this.path}/${id}`, {
      filter,
    });

    return this.http.get<Cluster>(url)
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item) => {
          return item
            ? new ClusterModel(item)
            : null;
        }),
        tap((item) => {
          this.updateItem(item);
        }));
  }
}
